import React, { useState, useEffect } from 'react';
import { emailTemplatesAPI } from '../lib/api';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Send, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_DISPLAY_NAMES } from '../lib/utils';
import { EmailModeToggle } from './EmailModeToggle';
import { EmailPreviewDialog } from './EmailPreviewDialog';
import { resolveBodyHtml, replaceCustomerVars } from '../lib/emailEditor';

export const LeadSendEmailDialog = ({ open, onOpenChange, lead, leadSource, onEmailSent }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [editorMode, setEditorMode] = useState('text');
  const [previewOpen, setPreviewOpen] = useState(false);

  const email = lead?.email || lead?.sender_email || '';
  const name = lead?.name || lead?.sender_name || `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim() || '';

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSelectedTemplate('none');
      setSubject('');
      setBody('');
      setFromEmail('');
      setEditorMode('text');
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data || []);
    } catch { /* ignore */ }
  };

  const handleTemplateChange = (val) => {
    setSelectedTemplate(val);
    if (val === 'none') {
      setSubject('');
      setBody('');
      return;
    }
    const tmpl = templates.find(t => t.trigger === val);
    if (tmpl) {
      const leadCtx = { name, first_name: lead?.first_name, last_name: lead?.last_name, email, phone: lead?.phone };
      setSubject(replaceCustomerVars(tmpl.subject || '', leadCtx));
      setBody(replaceCustomerVars(tmpl.body_html || '', leadCtx));
      // Templates are authored as HTML — flip the editor so the user sees markup, not garbled text
      setEditorMode('html');
    }
  };

  const performSend = async () => {
    setSending(true);
    try {
      await api.post('/leads/send-email', {
        to_email: email,
        to_name: name,
        subject,
        body_html: resolveBodyHtml(body, editorMode),
        from_email: fromEmail,
        cc_emails: '',
        lead_id: lead?.id || '',
        lead_source: leadSource || '',
      });
      toast.success(`Email sent to ${email}`);
      onOpenChange(false);
      onEmailSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    await performSend();
  };

  const handlePreview = () => {
    if (!subject.trim() && !body.trim()) {
      toast.error('Add a subject or body before previewing');
      return;
    }
    setPreviewOpen(true);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="lead-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" /> Send Email to {name || email}
          </DialogTitle>
          <DialogDescription>Send an email using a template or write a custom message.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* To + From */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-500">To</Label>
              <Input value={email} disabled className="bg-slate-50 text-sm" data-testid="lead-email-to" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-500">From</Label>
              <Select value={fromEmail || 'default'} onValueChange={(v) => setFromEmail(v === 'default' ? '' : v)}>
                <SelectTrigger data-testid="lead-email-from"><SelectValue placeholder="Default mailbox" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default mailbox</SelectItem>
                  <SelectItem value="hello@helioses.co.uk">hello@helioses.co.uk</SelectItem>
                  <SelectItem value="noreply@helioses.co.uk">noreply@helioses.co.uk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Template */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-500">Template</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger data-testid="lead-email-template"><SelectValue placeholder="Select template..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template (blank)</SelectItem>
                {templates.filter(t => t.active !== false).map(t => (
                  <SelectItem key={t.trigger} value={t.trigger}>
                    {STATUS_DISPLAY_NAMES[t.trigger] || t.trigger}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-500">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject..." data-testid="lead-email-subject" />
          </div>

          {/* Body + mode toggle */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-slate-500">Message</Label>
              <EmailModeToggle
                mode={editorMode}
                onChange={setEditorMode}
                body={body}
                setBody={setBody}
                testIdPrefix="lead-editor-mode"
              />
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={editorMode === 'html'
                ? '<p>Dear {{first_name}},</p>\n\n<p>...</p>'
                : `Dear ${name || 'Customer'},\n\nType your message here...\n\nKind regards,\nHelios Electrical & Solar`}
              className={`w-full min-h-[180px] p-3 text-sm border border-slate-200 rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${editorMode === 'html' ? 'font-mono bg-slate-900 text-emerald-300' : 'bg-white'}`}
              spellCheck={editorMode === 'text'}
              data-testid="lead-email-body"
            />
            <p className="text-xs text-slate-400">
              {editorMode === 'html' ? 'HTML mode — paste markup from your designer or hand-write it.' : 'Plain text — line breaks become paragraphs in the email.'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={!subject.trim() && !body.trim()}
              data-testid="lead-email-preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !email || !subject.trim() || !body.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="lead-email-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <EmailPreviewDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      to={email}
      from={fromEmail}
      subject={subject}
      bodyHtml={resolveBodyHtml(body, editorMode)}
      onConfirmSend={handleSend}
      sending={sending}
      recipientLabel={name?.split(' ')[0] || 'the lead'}
    />
    </>
  );
};

export default LeadSendEmailDialog;
